import React, { useState } from "react";
import axios from "axios";

const ServiceImportModal = ({ isOpen, onClose, onSuccess, businessId }) => {
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState("");
  const [results, setResults] = useState(null);

  const API_BASE_URL =
    process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (
      selectedFile &&
      (selectedFile.type === "text/csv" || selectedFile.name.endsWith(".csv"))
    ) {
      setFile(selectedFile);
      setError("");
      setResults(null);
    } else {
      setError("Please select a valid CSV file");
      setFile(null);
    }
  };

  const handleImport = async () => {
    if (!file) {
      setError("Please select a CSV file");
      return;
    }

    if (!businessId) {
      setError("Business ID not found");
      return;
    }

    try {
      setImporting(true);
      setError("");

      const token = localStorage.getItem("ogestyle_token");
      if (!token) {
        window.location.href = "/login";
        return;
      }

      const formData = new FormData();
      formData.append("file", file);
      formData.append("dataType", "services");
      formData.append("sourcePlatform", "square");

      // Use the import engine endpoint
      const response = await axios.post(
        `${API_BASE_URL}/api/import/business/${businessId}/prepare-import`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        },
      );

      // If successful, execute the import
      if (response.data.success) {
        const executeFormData = new FormData();
        executeFormData.append("file", file);
        executeFormData.append("dataType", "services");
        executeFormData.append("sourcePlatform", "square");
        if (response.data.mapping) {
          executeFormData.append(
            "mapping",
            JSON.stringify(response.data.mapping),
          );
        }
        const executeResponse = await axios.post(
          `${API_BASE_URL}/api/import/business/${businessId}/execute-import`,
          executeFormData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          },
        );

        setResults(executeResponse.data);
        if (onSuccess) {
          setTimeout(() => {
            onSuccess();
            handleClose();
          }, 2000);
        }
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Failed to import services. Please try again.",
      );
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setError("");
    setResults(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
      }}
      onClick={handleClose}
    >
      <div
        style={{
          background: "white",
          borderRadius: "12px",
          padding: "32px",
          maxWidth: "500px",
          width: "90%",
          maxHeight: "80vh",
          overflow: "auto",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{ margin: "0 0 20px 0", fontSize: "24px" }}>
          📁 Import Services
        </h2>

        {!results ? (
          <>
            <p style={{ margin: "0 0 20px 0", color: "#6b7280" }}>
              Upload a CSV file containing service information. The file should
              include columns for name, description, price, duration, and
              category.
            </p>

            <div style={{ marginBottom: "20px" }}>
              <input
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                style={{
                  width: "100%",
                  padding: "12px",
                  border: "2px dashed #d1d5db",
                  borderRadius: "8px",
                  cursor: "pointer",
                }}
              />
              {file && (
                <p
                  style={{
                    margin: "8px 0 0 0",
                    fontSize: "14px",
                    color: "#10b981",
                  }}
                >
                  ✓ {file.name}
                </p>
              )}
            </div>

            {error && (
              <div
                style={{
                  padding: "12px",
                  background: "#fef2f2",
                  border: "1px solid #fecaca",
                  borderRadius: "8px",
                  color: "#dc2626",
                  marginBottom: "20px",
                }}
              >
                {error}
              </div>
            )}

            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "flex-end",
              }}
            >
              <button
                onClick={handleClose}
                disabled={importing}
                style={{
                  padding: "10px 20px",
                  background: "white",
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "14px",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleImport}
                disabled={!file || importing}
                style={{
                  padding: "10px 20px",
                  background: file && !importing ? "#3b82f6" : "#d1d5db",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: file && !importing ? "pointer" : "not-allowed",
                  fontSize: "14px",
                }}
              >
                {importing ? "Importing..." : "Import Services"}
              </button>
            </div>
          </>
        ) : (
          <>
            <div
              style={{
                padding: "20px",
                background: "#f0fdf4",
                border: "1px solid #bbf7d0",
                borderRadius: "8px",
                marginBottom: "20px",
              }}
            >
              <p
                style={{
                  margin: "0 0 10px 0",
                  fontWeight: "bold",
                  color: "#16a34a",
                }}
              >
                ✓ Import Complete
              </p>
              <p style={{ margin: "0", fontSize: "14px", color: "#15803d" }}>
                Successfully imported {results.summary?.created || 0}{" "}
                service(s).
                {results.summary?.skipped > 0 &&
                  ` (${results.summary.skipped} skipped)`}
              </p>
            </div>
            <button
              onClick={handleClose}
              style={{
                width: "100%",
                padding: "10px 20px",
                background: "#3b82f6",
                color: "white",
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
                fontSize: "14px",
              }}
            >
              Close
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default ServiceImportModal;
